import { type PhoneCode } from '../countries/types';
import {
  type AxiosCompatibleError,
  type GenericErrorResponse,
} from '../http/types';

import { type UsernameValidityErrorResponse } from './aliases';
import {
  SIGNUP_ERROR_TEXT,
  USERNAME_VALIDITY_ERROR_CODE_MAP,
} from './constants';
import {
  type SignupErrorDetails,
  SignupErrorTypes,
  type UserSignupDetails,
} from './types';
import { type CombinedSignupFormValues } from './validationSchema';

import { PHONE_CODES, countries } from '@/modules/countries/constants';
import { parseDateParts } from '@/modules/date/helpers';

export function findLocationLabel(location: string) {
  return countries.find((country) => country.code === location.toUpperCase())
    ?.name;
}

export function getDefaultCountry(storedCountry?: string): PhoneCode {
  if (!storedCountry) {
    return PHONE_CODES.US;
  }

  return PHONE_CODES[storedCountry.toUpperCase()] || PHONE_CODES.US;
}

export function getErrorType(errorType?: string) {
  if (errorType?.includes('USERNAME')) {
    return SignupErrorTypes.Username;
  }
  if (errorType === 'AGE_TOO_YOUNG') {
    return SignupErrorTypes.Dob;
  }
  if (errorType?.includes('UNKNOWN')) {
    return SignupErrorTypes.UnknownError;
  }
  return SignupErrorTypes.OtherError;
}

export async function parseErrorDetails(
  err: AxiosCompatibleError<GenericErrorResponse>
): Promise<SignupErrorDetails> {
  const error = err.response.data?.message;
  const errorType = getErrorType(error);

  return {
    errorType,
    errorMessage: SIGNUP_ERROR_TEXT[error]
      ? SIGNUP_ERROR_TEXT[error]
      : 'GenericError',
  };
}

export function isUnderAgeSignupError(
  err: AxiosCompatibleError<GenericErrorResponse>
) {
  return err.response.data?.message === 'AGE_TOO_YOUNG';
}

export function isUsernameValidityError(
  err: AxiosCompatibleError
): err is AxiosCompatibleError<
  UsernameValidityErrorResponse & {
    code: keyof typeof USERNAME_VALIDITY_ERROR_CODE_MAP;
  }
> {
  const { data } = err.response;

  if (!data || typeof data !== 'object' || !('code' in data)) {
    return false;
  }

  if (
    typeof data.code === 'number' &&
    data.code in USERNAME_VALIDITY_ERROR_CODE_MAP
  ) {
    return true;
  }

  return false;
}

export function validateDobRange(
  min: number,
  max: number,
  errorMessage: string
) {
  return (value: string | undefined) => {
    if (!value) {
      return true;
    }
    const num = Number(value);
    if (!Number.isInteger(num) || num < min || num > max) {
      return errorMessage;
    }
    return true;
  };
}

export function validateDay(
  _value: string | undefined,
  formValues: UserSignupDetails
) {
  const { dobDay: day, dobMonth: month, dobYear: year } = formValues;

  // A half filled date of birth isn't wrong yet, so don't complain about it.
  if (!Number(day) || !Number(month) || !Number(year)) {
    return true;
  }

  if (!parseDateParts({ day, month, year })) {
    return 'DateOfBirth.DayRequired';
  }

  return true;
}

export function normalizeEmailLocalPart(email: string): string {
  if (!email || !email.includes('@')) {
    return '';
  }

  let [localPart] = email.split('@');

  // Remove everything after + (email aliases)
  if (localPart.includes('+')) {
    [localPart] = localPart.split('+');
  }

  return localPart
    .toLowerCase()
    .replace(/\./g, '_')
    .replace(/[^A-Za-z0-9_]/g, '');
}

export function transformCombinedSignupFormValuesToSignupRequest(
  formValues: CombinedSignupFormValues
): UserSignupDetails {
  return {
    ...formValues,
    username: formValues.username ?? '',
  };
}
